import { useUploadFileMutation } from 'store/services/documentStorage/googleCloudStorage';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';

import type { FileListEvent, FileListEventTarget } from 'components/user/Records/components/FileUploadsItem';
import { DEFAULT_MAX_FILE_SIZE_MB } from 'components/user/Records/components/FileUploadsItem';
import type { FileResponse } from 'types';
import { normalizeUploadName } from 'utils/file/fileUtils';
import { useFlowFilesData } from './useFlowFilesData';
import { parseMaxFilesNumber, toFileIds, type FileUploadValue } from '../utils/toFileIds';

interface UseFileEditModalParams<K extends FieldValues> {
  open: boolean;
  onClose: () => void;
  setValue: UseFormSetValue<K>;
  fieldName: Path<K>;
  value?: FileUploadValue;
  maxFilesNumber?: number | string | null;
}

export const useFileEditModal = <K extends FieldValues>({ open, onClose, setValue, fieldName, value, maxFilesNumber }: UseFileEditModalParams<K>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileIds, setFileIds] = useState<string[]>(() => toFileIds(value));
  const [uploadedFilesData, setUploadedFilesData] = useState<FileResponse[]>([]);
  const [uploadError, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadFile] = useUploadFileMutation();
  const maxFiles = useMemo(() => parseMaxFilesNumber(maxFilesNumber), [maxFilesNumber]);

  useEffect(() => {
    if (!open) return;

    setFileIds(toFileIds(value));
    setUploadedFilesData([]);
    setError(null);
  }, [open, value]);

  const { filesData, isFetching } = useFlowFilesData({
    ids: fileIds,
    skip: !open,
    fallbackFiles: uploadedFilesData,
  });

  const isDisableUpload = maxFiles !== null && fileIds.length >= maxFiles;
  const showLoader = fileIds.length > 0 && filesData.length === 0 && (isFetching || isUploading);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFileIds((prev) => prev.filter((id) => id !== fileId));
    setUploadedFilesData((prev) => prev.filter((file) => file.id !== fileId));
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (isUploading) return;

    setValue(fieldName, fileIds.map((fileId) => ({ fileId })) as unknown as K[typeof fieldName], { shouldDirty: true });
    handleClose();
  }, [isUploading, setValue, fieldName, fileIds, handleClose]);

  const handleButtonOpenUpload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!inputRef.current || isDisableUpload || isUploading) return;
      inputRef.current.value = '';
      inputRef.current.click();
    },
    [isDisableUpload, isUploading]
  );

  const handleUploadFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement> | FileListEvent) => {
      if (isUploading) return;

      const selectedFiles = Array.from(event.target.files || []);
      if (selectedFiles.length === 0) return;

      let filesToUpload = selectedFiles;

      if (maxFiles !== null) {
        const remainingSlots = maxFiles - fileIds.length;
        if (remainingSlots <= 0) {
          setError(t('user.detail.file.maxFilesReached', { max: maxFiles }));
          return;
        }
        if (filesToUpload.length > remainingSlots) {
          setError(
            t('user.detail.file.onlySomeFilesUploaded', {
              uploaded: remainingSlots,
              total: filesToUpload.length,
              max: maxFiles,
            })
          );
          filesToUpload = filesToUpload.slice(0, remainingSlots);
        } else {
          setError(null);
        }
      }

      const oversizedFiles = filesToUpload.filter((file) => file.size > DEFAULT_MAX_FILE_SIZE_MB * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setError(`${t('common.maxSize')} ${DEFAULT_MAX_FILE_SIZE_MB}MB`);
        return;
      }

      try {
        setIsUploading(true);
        const uploaded: FileResponse[] = [];

        for (const file of filesToUpload) {
          const normalizedName = normalizeUploadName(file.name);
          const formData = new FormData();
          formData.append('file', file, normalizedName);

          const res = await uploadFile({
            data: formData,
            name: normalizedName,
          });

          if (res.data) {
            uploaded.push(res.data);
          } else {
            setError(t('user.detail.file.uploadFailed', { fileName: normalizedName }));
          }
        }

        if (uploaded.length > 0) {
          setUploadedFilesData((prev) => [...prev, ...uploaded]);
          setFileIds((prev) => [...prev, ...uploaded.map((file) => file.id)]);
          toast.success(t('common.uploadFileSuccess'));
        }
      } catch {
        toast.error(t('common.uploadFileError'));
      } finally {
        setIsUploading(false);
      }
    },
    [fileIds.length, isUploading, maxFiles, t, uploadFile]
  );

  const onDrop = useCallback(
    (data: File[]) =>
      handleUploadFile({
        target: { files: data } as FileListEventTarget & HTMLInputElement,
      }),
    [handleUploadFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    disabled: isDisableUpload || isUploading,
    maxSize: DEFAULT_MAX_FILE_SIZE_MB * 1024 * 1024,
  });

  return {
    filesData,
    uploadError,
    isUploading,
    isDisableUpload,
    showLoader,
    inputRef,
    getRootProps,
    getInputProps,
    handleRemoveFile,
    handleClose,
    handleSubmit,
    handleButtonOpenUpload,
    handleUploadFile,
  };
};
